import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Percent, Trophy, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ReferenceLine,
} from "recharts";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

const NAVY = "#0d1a3f";
const RED = "#E3000F";
const YELLOW = "#FFD100";
const BLUE = "#005BBB";
const GREEN = "#2E7D32";
const AMBER = "#B26A00";

const StatCard = ({ Icon, label, value, color = BLUE }) => (
  <div className="brutal p-5 bg-white flex items-center gap-4">
    <div
      className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center"
      style={{ background: color }}
    >
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div>
      <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
        {label}
      </div>
      <div className="font-display font-black text-3xl leading-none mt-1">{value}</div>
    </div>
  </div>
);

const barColor = (v) => (v >= 80 ? GREEN : v >= 60 ? AMBER : RED);

const truncate = (s, n = 26) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

const CustomTooltip = ({ active, payload, label, lang, kind }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="brutal-sm bg-white p-3 text-xs font-body">
      <div className="font-display font-black text-sm mb-1">{label}</div>
      <div>
        {lang === "es" ? "Aprobación" : "Pass rate"}:{" "}
        <span className="font-bold">{row.pass_rate}%</span>
      </div>
      <div>
        {lang === "es" ? "Promedio" : "Average"}:{" "}
        <span className="font-bold">{row.avg_percent}%</span>
      </div>
      <div>
        {lang === "es" ? "Intentos" : "Attempts"}:{" "}
        <span className="font-bold">{row.attempts}</span>
      </div>
      {kind === "exam" && row.passing_score != null && (
        <div className="text-neutral-500 mt-1">
          {lang === "es" ? "Nota mínima" : "Passing score"}: {row.passing_score}%
        </div>
      )}
    </div>
  );
};

export default function Stats() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("pass_rate"); // 'pass_rate' | 'avg_percent'

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats/breakdown");
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Error");
      }
    })();
  }, []);

  const examData = useMemo(
    () =>
      (data?.by_exam || []).map((x) => ({
        ...x,
        title: truncate(lang === "es" ? x.title_es : x.title_en, 28),
      })),
    [data, lang]
  );
  const roleData = useMemo(
    () =>
      (data?.by_role || []).map((x) => ({
        ...x,
        title: truncate(x.name, 28),
      })),
    [data]
  );

  const metricLabel =
    metric === "pass_rate"
      ? lang === "es"
        ? "% de aprobación"
        : "Pass rate"
      : lang === "es"
      ? "Promedio"
      : "Average score";

  if (error) {
    return (
      <div className="brutal p-6 bg-red-50 font-body text-canales-red">
        {error}
      </div>
    );
  }
  if (!data) {
    return <div className="font-display" data-testid="stats-loading">Loading stats…</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-stats-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-black text-4xl">
          {lang === "es" ? "Estadísticas" : "Statistics"}
        </h1>
        <div className="inline-flex brutal-sm p-1 bg-white" data-testid="stats-metric-toggle">
          <button
            data-testid="stats-metric-passrate"
            onClick={() => setMetric("pass_rate")}
            className={`px-3 py-1 font-display font-bold text-sm rounded-full ${
              metric === "pass_rate" ? "bg-canales-red text-white" : "text-black"
            }`}
          >
            {lang === "es" ? "% Aprobación" : "Pass rate"}
          </button>
          <button
            data-testid="stats-metric-avg"
            onClick={() => setMetric("avg_percent")}
            className={`px-3 py-1 font-display font-bold text-sm rounded-full ${
              metric === "avg_percent" ? "bg-canales-blue text-white" : "text-black"
            }`}
          >
            {lang === "es" ? "Promedio" : "Average"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          Icon={BarChart3}
          label={lang === "es" ? "Intentos totales" : "Total attempts"}
          value={data.totals.attempts}
          color={NAVY}
        />
        <StatCard
          Icon={Trophy}
          label={lang === "es" ? "Aprobados" : "Passed"}
          value={data.totals.passed}
          color={GREEN}
        />
        <StatCard
          Icon={Percent}
          label={lang === "es" ? "% Aprobación global" : "Global pass rate"}
          value={`${data.totals.pass_rate}%`}
          color={RED}
        />
        <StatCard
          Icon={TrendingUp}
          label={lang === "es" ? "Promedio global" : "Global average"}
          value={`${data.totals.avg_percent}%`}
          color={BLUE}
        />
      </div>

      {/* By Exam */}
      <section className="brutal p-6 bg-white" data-testid="stats-by-exam">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-display font-black text-2xl">
            {lang === "es" ? "Por examen" : "By exam"}
          </h2>
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {metricLabel}
          </div>
        </div>
        {examData.length === 0 ? (
          <p className="font-body text-neutral-600">
            {lang === "es" ? "Aún no hay intentos." : "No attempts yet."}
          </p>
        ) : (
          <>
            <div style={{ width: "100%", height: Math.max(260, examData.length * 42) }}>
              <ResponsiveContainer>
                <BarChart
                  data={examData}
                  layout="vertical"
                  margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    stroke={NAVY}
                  />
                  <YAxis
                    dataKey="title"
                    type="category"
                    width={180}
                    stroke={NAVY}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    content={<CustomTooltip lang={lang} kind="exam" />}
                    cursor={{ fill: "#00000010" }}
                  />
                  <ReferenceLine x={70} stroke={NAVY} strokeDasharray="4 4" />
                  <Bar dataKey={metric} radius={[0, 6, 6, 0]}>
                    {examData.map((row, i) => (
                      <Cell key={i} fill={barColor(row[metric])} stroke="#000" strokeWidth={2} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm font-body" data-testid="stats-by-exam-table">
                <thead>
                  <tr className="text-left text-neutral-600 uppercase text-xs font-display font-bold tracking-widest">
                    <th className="py-2 pr-3">{lang === "es" ? "Examen" : "Exam"}</th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "Intentos" : "Attempts"}
                    </th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "% Aprobación" : "Pass %"}
                    </th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "Promedio" : "Average"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {examData.map((r) => (
                    <tr key={r.exam_id} className="border-t border-black/10">
                      <td className="py-2 pr-3">
                        {lang === "es" ? r.title_es : r.title_en}
                      </td>
                      <td className="py-2 pr-3 text-right">{r.attempts}</td>
                      <td className="py-2 pr-3 text-right font-bold" style={{ color: barColor(r.pass_rate) }}>
                        {r.pass_rate}%
                      </td>
                      <td className="py-2 pr-3 text-right">{r.avg_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* By Role */}
      <section className="brutal p-6 bg-white" data-testid="stats-by-role">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-display font-black text-2xl">
            {lang === "es" ? "Por rol" : "By role"}
          </h2>
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {metricLabel}
          </div>
        </div>
        {roleData.length === 0 ? (
          <p className="font-body text-neutral-600">
            {lang === "es"
              ? "Aún no hay roles configurados. Crea uno en Roles."
              : "No roles configured yet."}
          </p>
        ) : (
          <>
            <div style={{ width: "100%", height: Math.max(220, roleData.length * 48) }}>
              <ResponsiveContainer>
                <BarChart
                  data={roleData}
                  layout="vertical"
                  margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    stroke={NAVY}
                  />
                  <YAxis
                    dataKey="title"
                    type="category"
                    width={180}
                    stroke={NAVY}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    content={<CustomTooltip lang={lang} kind="role" />}
                    cursor={{ fill: "#00000010" }}
                  />
                  <ReferenceLine x={70} stroke={NAVY} strokeDasharray="4 4" />
                  <Bar dataKey={metric} radius={[0, 6, 6, 0]}>
                    {roleData.map((row, i) => (
                      <Cell
                        key={i}
                        fill={row.attempts === 0 ? "#CFD3DA" : barColor(row[metric])}
                        stroke="#000"
                        strokeWidth={2}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm font-body" data-testid="stats-by-role-table">
                <thead>
                  <tr className="text-left text-neutral-600 uppercase text-xs font-display font-bold tracking-widest">
                    <th className="py-2 pr-3">{lang === "es" ? "Rol" : "Role"}</th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "Exámenes" : "Exams"}
                    </th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "Intentos" : "Attempts"}
                    </th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "% Aprobación" : "Pass %"}
                    </th>
                    <th className="py-2 pr-3 text-right">
                      {lang === "es" ? "Promedio" : "Average"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleData.map((r) => (
                    <tr key={r.role_slug} className="border-t border-black/10">
                      <td className="py-2 pr-3">{r.name}</td>
                      <td className="py-2 pr-3 text-right">
                        {r.mandatory_count + r.bonus_count}
                      </td>
                      <td className="py-2 pr-3 text-right">{r.attempts}</td>
                      <td
                        className="py-2 pr-3 text-right font-bold"
                        style={{
                          color: r.attempts === 0 ? "#6b7280" : barColor(r.pass_rate),
                        }}
                      >
                        {r.attempts === 0 ? "—" : `${r.pass_rate}%`}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {r.attempts === 0 ? "—" : `${r.avg_percent}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <div className="text-xs font-body text-neutral-500 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-black" style={{ background: GREEN }} /> ≥ 80%
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-black" style={{ background: AMBER }} /> 60–79%
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-black" style={{ background: RED }} /> &lt; 60%
        </span>
        <span className="ml-2">
          {lang === "es"
            ? "Línea punteada = nota mínima de aprobación (70%)"
            : "Dashed line = passing threshold (70%)"}
        </span>
      </div>
      {/* keep translation hook referenced (avoids unused-var lint) */}
      <span className="sr-only">{t("dashboard")}</span>
    </motion.div>
  );
}
